// deps
import React, { memo, useMemo } from "react";

// internal-deps
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as ArrowBack } from "@swiggy-private/dls-web-icons/dist/web/generic/arrowBack/24";
import { default as Delete } from "@swiggy-private/dls-web-icons/dist/web/generic/delete/16";
import { default as Refresh } from "@swiggy-private/dls-web-icons/dist/web/generic/refresh/12";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { format } from "@swiggy-private/js-utils";
import { GROUP_CART_ORDER_DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";

// styles
import {
    Container,
    IconContainer,
    Pill,
    PillsContainer,
    RestaurantNameText,
    TextWrapper,
    TitleContainer,
    TopContainer,
} from "./CartGroupOrderHeader.style";

// constants
import {
    HEADER_MULTIPLE_MEMBER_SUBTITLE,
    HEADER_SINGLE_MEMBER_SUBTITLE,
    HEADER_TITLE,
    INVITE_GROUP_ICON_URL,
    INVITE_MORE,
    LEAVE_GROUP,
    LEAVE_GROUP_ICON_URL,
    REFRESH,
    YOUR_GROUP_ORDER,
} from "@cart-ui/constants/GroupCart";
import { RAIN_BG_COLOR } from "@cart-ui/constants";

// components
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

interface CartGroupOrderHeaderProps {
    isHost: boolean;
    restaurantName: string;
    totalPeopleInGroup: number;
    hostFirstName: string;
    restaurantArea: string;
    handleBack: VoidFunction;
    handleInviteMore: VoidFunction;
    handleDeleteGroupCart: VoidFunction;
    handleLeaveGroupCart: VoidFunction;
    handleRefreshGroupCart: VoidFunction;
}

const LEAVE_GROUP_PILL_ICON_DIMS = {
    width: "12px",
    height: "12px",
};

const INVITE_GROUP_PILL_ICON_DIMS = {
    width: "13px",
    height: "13px",
};

const CartGroupOrderHeader = ({
    isHost,
    restaurantName,
    totalPeopleInGroup,
    hostFirstName,
    restaurantArea,
    handleBack,
    handleInviteMore,
    handleDeleteGroupCart,
    handleLeaveGroupCart,
    handleRefreshGroupCart,
}: CartGroupOrderHeaderProps) => {
    const groupOrderMetaTemplate = useMemo(
        () => (totalPeopleInGroup == 1 ? HEADER_SINGLE_MEMBER_SUBTITLE : HEADER_MULTIPLE_MEMBER_SUBTITLE),
        [totalPeopleInGroup],
    );

    const renderDeleteCta = useMemo(() => {
        if (isHost) {
            return (
                <Pill
                    isHost={isHost}
                    onClick={handleDeleteGroupCart}
                    data-testid={GROUP_CART_ORDER_DATA_TEST_IDS.DELETE_GROUP_PILL}
                >
                    <IconContainer>
                        <GenericIcon Icon={Delete} strokeColor="negative" fillColor="negative" />
                    </IconContainer>
                </Pill>
            );
        }

        return (
            <Pill
                isHost={isHost}
                onClick={handleLeaveGroupCart}
                data-testid={GROUP_CART_ORDER_DATA_TEST_IDS.LEAVE_GROUP_PILL}
            >
                <IconContainer>
                    <Image {...LEAVE_GROUP_PILL_ICON_DIMS} cloudinaryId={LEAVE_GROUP_ICON_URL} alt="Leave-Group-Cart" />
                </IconContainer>
                <TextWrapper variant="V2.body3.neutral.bold" color="negative">
                    {LEAVE_GROUP}
                </TextWrapper>
            </Pill>
        );
    }, [isHost, handleDeleteGroupCart, handleLeaveGroupCart]);

    return (
        <Container>
            <TopContainer>
                <GenericIcon
                    Icon={ArrowBack}
                    onClick={handleBack}
                    data-testid={GROUP_CART_ORDER_DATA_TEST_IDS.BACK_ICON}
                />
                <TitleContainer>
                    <Text
                        variant="V2.subheading1.neutral.bold"
                        data-testid={GROUP_CART_ORDER_DATA_TEST_IDS.HEADER_TITLE}
                    >
                        {isHost ? YOUR_GROUP_ORDER : format(HEADER_TITLE, hostFirstName)}
                    </Text>
                    <RestaurantNameText
                        variant="V2.body2.neutral.regular"
                        color="text_Med_Emphasis"
                        data-testid={GROUP_CART_ORDER_DATA_TEST_IDS.HEADER_SUBTITLE}
                    >
                        {format(groupOrderMetaTemplate, totalPeopleInGroup, restaurantName, restaurantArea)}
                    </RestaurantNameText>
                </TitleContainer>
            </TopContainer>
            <PillsContainer>
                <Pill
                    backgroundColor={RAIN_BG_COLOR}
                    onClick={handleInviteMore}
                    data-testid={GROUP_CART_ORDER_DATA_TEST_IDS.INVITE_MORE_PILL}
                >
                    <IconContainer>
                        <Image
                            {...INVITE_GROUP_PILL_ICON_DIMS}
                            cloudinaryId={INVITE_GROUP_ICON_URL}
                            alt="Invite-More-To-Groupcart"
                        />
                    </IconContainer>

                    <TextWrapper variant="V2.body3.neutral.bold" color={RAIN_BG_COLOR}>
                        {INVITE_MORE}
                    </TextWrapper>
                </Pill>

                <Pill
                    backgroundColor="background_Secondary"
                    data-testid={GROUP_CART_ORDER_DATA_TEST_IDS.REFRESH_CTA}
                    onClick={handleRefreshGroupCart}
                >
                    <IconContainer>
                        <GenericIcon
                            style={{ lineHeight: 1 }}
                            Icon={Refresh}
                            strokeColor="text_High_Emphasis"
                            fillColor="text_High_Emphasis"
                        />
                    </IconContainer>

                    <TextWrapper variant="V2.body3.neutral.bold" color="text_High_Emphasis">
                        {REFRESH}
                    </TextWrapper>
                </Pill>

                {renderDeleteCta}
            </PillsContainer>
        </Container>
    );
};

export default memo(CartGroupOrderHeader);
